import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Sidebar from '../common/Sidebar';
import PaginationControls from '../common/PaginationControls';
import ValidationInterface from '../chef/ValidationInterface';
import NoticeToast from '../common/NoticeToast';
import { useAuth } from '../../auth/AuthContext';
import { BRAND_ASSETS } from '../../branding';
import { apiUrl } from '../../config/api';
import { apiFetch } from '../../api/client';
import { useNotice } from '../../hooks/useNotice';
import { openOrDownloadDocument, safeFilename } from '../../utils/documentHandling';
import './ConseillerDashboard.css';

const ITEMS_PER_PAGE = 10;

export default function ConseillerDashboard({ showInlineLogout = true }) {
  const { user, logout } = useAuth();
  const { notice, showNotice, clearNotice } = useNotice();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [timelinePlan, setTimelinePlan] = useState(null);
  const [timelinePlanData, setTimelinePlanData] = useState(null);
  const [planPayload, setPlanPayload] = useState(null);
  const [loadingPayload, setLoadingPayload] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [activeSection, setActiveSection] = useState('plans');
  const [currentPage, setCurrentPage] = useState(1);
  const plansSectionRef = useRef(null);
  const statsSectionRef = useRef(null);
  const validationSectionRef = useRef(null);
  const [filters, setFilters] = useState({
    teacher: '',
    course: '',
    chef: '',
    status: '', // Tous les statuts par défaut
    session: '',
    programme: '',
    mode: '',
    campus: '',
    sortBy: 'date_soumission',
    sortDirection: 'desc'
  });

  const scrollToSection = useCallback((sectionRef) => {
    if (!sectionRef?.current) return;

    const topOffset = 88;
    const targetTop = sectionRef.current.getBoundingClientRect().top + window.scrollY - topOffset;
    window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
  }, []);

  const handleNavigate = useCallback((sectionKey) => {
    setActiveSection(sectionKey);

    const sectionTargets = {
      plans: plansSectionRef,
      stats: statsSectionRef,
      form: validationSectionRef,
    };

    scrollToSection(sectionTargets[sectionKey]);
  }, [scrollToSection]);

  useEffect(() => {
    const loadPlans = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await apiFetch('/api/plans/chefs-soumis', {
          auth: true,
        });
        if (!response.ok) throw new Error('Erreur lors du chargement des plans');
        const data = await response.json();
        setPlans(data);
      } catch (err) {
        setError('Impossible de charger les plans.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, [refreshToken]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSortChange = (sortBy, sortDirection) => {
    if (!sortDirection) {
      setFilters((prev) => ({ ...prev, sortBy: 'date_soumission', sortDirection: 'desc' }));
      return;
    }
    setFilters((prev) => ({ ...prev, sortBy, sortDirection }));
  };

  const getSortIndicator = (column) => {
    if (filters.sortBy !== column) {
      return '↕';
    }
    return filters.sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleRefresh = () => {
    setRefreshToken((current) => current + 1);
  };

  const handleSelectPlan = async (plan) => {
    setSelectedPlan(plan);
    setPlanPayload(null);
  };

  const handleOpenTimeline = async (plan) => {
    setTimelinePlan(plan);
    setTimelinePlanData(null);

    try {
      const response = await apiFetch(`/api/plans/${plan.id}/grille-data`, {
        auth: true,
      });
      if (response.ok) {
        const data = await response.json();
        setTimelinePlanData(data);
      }
    } catch (err) {
      console.error('Erreur lors du chargement de la chronologie:', err);
    }
  };

  const handleCloseTimeline = () => {
    setTimelinePlan(null);
    setTimelinePlanData(null);
  };

  useEffect(() => {
    const loadPayload = async () => {
      if (!selectedPlan?.id) {
        setPlanPayload(null);
        return;
      }

      setLoadingPayload(true);
      try {
        const response = await apiFetch(`/api/plans/${selectedPlan.id}/grille-data`, {
          auth: true,
        });
        if (!response.ok) {
          throw new Error('Erreur lors du chargement de la grille');
        }
        const data = await response.json();
        setPlanPayload(data.payload || {});
      } catch (err) {
        console.error('Erreur chargement payload conseiller:', err);
        setPlanPayload({});
      } finally {
        setLoadingPayload(false);
      }
    };

    loadPayload();
  }, [selectedPlan?.id]);

  const sessions = useMemo(() => {
    const unique = new Set(plans.map(p => p.session).filter(s => s && s !== 'N/A'));
    return ['Toutes', ...Array.from(unique)];
  }, [plans]);

  const programmes = useMemo(() => {
    const unique = new Set(plans.map(p => p.programme).filter(p => p && p !== 'N/A'));
    return ['Tous', ...Array.from(unique)];
  }, [plans]);

  const modes = useMemo(() => {
    const unique = new Set(plans.map(p => p.mode_enseignement).filter(m => m && m !== 'N/A'));
    return ['Tous', ...Array.from(unique)];
  }, [plans]);

  const campus = useMemo(() => {
    const unique = new Set(plans.map(p => p.campus).filter(c => c && c !== 'N/A'));
    return ['Tous', ...Array.from(unique)];
  }, [plans]);

  const toDisplayStatus = useCallback((statusKey) => {
    if (statusKey === 'non_analyse') {
      return 'analyse';
    }
    return statusKey;
  }, []);

  const filteredPlans = useMemo(() => {
    const filtered = plans.filter(plan => {
    // Filter par statut
    const displayStatus = toDisplayStatus(plan.statusKey || plan.statut);
    if (filters.status && displayStatus !== filters.status) return false;
    
    // Filter par enseignant
    if (filters.teacher && !plan.enseignant_nom?.toLowerCase().includes(filters.teacher.toLowerCase())) {
      return false;
    }
    
    // Filter par cours
    if (filters.course && !plan.titre?.toLowerCase().includes(filters.course.toLowerCase())) {
      return false;
    }
    
    // Filter par chef d'équipe
    if (filters.chef && !plan.chef_nom?.toLowerCase().includes(filters.chef.toLowerCase())) {
      return false;
    }

    // Filter par session
    if (filters.session && plan.session !== filters.session) {
      return false;
    }

    // Filter par programme
    if (filters.programme && plan.programme !== filters.programme) {
      return false;
    }

    // Filter par mode
    if (filters.mode && plan.mode_enseignement !== filters.mode) {
      return false;
    }

    // Filter par campus
    if (filters.campus && plan.campus !== filters.campus) {
      return false;
    }
    
    return true;
    });

    const statusRank = {
      analyse: 0,
      'non validé': 1,
      'validé': 2,
      approuvé: 3,
      'non approuvé': 4,
    };

    const compareText = (left, right) => String(left || '').localeCompare(String(right || ''), 'fr', { sensitivity: 'base' });

    return filtered.sort((a, b) => {
      const directionFactor = filters.sortDirection === 'asc' ? 1 : -1;

      if (filters.sortBy === 'date_soumission') {
        const dateA = new Date(a.date_soumission || a.date_upload || 0).getTime();
        const dateB = new Date(b.date_soumission || b.date_upload || 0).getTime();
        if (dateA !== dateB) {
          return (dateA - dateB) * directionFactor;
        }
      } else if (filters.sortBy === 'statusKey') {
        const rankA = statusRank[toDisplayStatus(a.statusKey || a.statut)] ?? 999;
        const rankB = statusRank[toDisplayStatus(b.statusKey || b.statut)] ?? 999;
        if (rankA !== rankB) {
          return (rankA - rankB) * directionFactor;
        }
      } else {
        const valueA = a[filters.sortBy];
        const valueB = b[filters.sortBy];
        const textCompare = compareText(valueA, valueB);
        if (textCompare !== 0) {
          return textCompare * directionFactor;
        }
      }

      return b.id - a.id;
    });
  }, [plans, filters, toDisplayStatus]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredPlans.length / ITEMS_PER_PAGE));
  }, [filteredPlans.length]);

  const paginatedPlans = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredPlans.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPlans, currentPage]);

  const sidebarRecentItems = useMemo(() => {
    return [...plans]
      .sort((left, right) => {
        const leftDate = new Date(left.date_soumission || left.date_upload || 0).getTime();
        const rightDate = new Date(right.date_soumission || right.date_upload || 0).getTime();
        if (rightDate !== leftDate) {
          return rightDate - leftDate;
        }
        return (right.id || 0) - (left.id || 0);
      })
      .slice(0, 3)
      .map((plan) => ({
        id: plan.id,
        name: plan.numero_cours || plan.titre || `Plan #${plan.id}`,
        subtitle: plan.date_soumission
          ? `Soumis le ${new Date(plan.date_soumission).toLocaleDateString('fr-CA')}`
          : 'Date inconnue',
        metricValue: plan.statusKey === 'validé' ? 'Valide' : plan.statusKey === 'non validé' ? 'Rejete' : 'En cours',
        metricLabel: '',
      }));
  }, [plans]);

  const monthlyPerformance = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const finalStatuses = new Set(['validé', 'non validé']);

    const monthlyPlans = plans.filter((plan) => {
      const rawDate = plan.date_soumission || plan.date_upload;
      if (!rawDate) return false;
      const submittedDate = new Date(rawDate);
      return submittedDate.getMonth() === currentMonth && submittedDate.getFullYear() === currentYear;
    });

    const completedMonthlyPlans = monthlyPlans.filter((plan) => finalStatuses.has((plan.statusKey || plan.statut || '').toLowerCase()));
    const rate = monthlyPlans.length > 0
      ? Math.round((completedMonthlyPlans.length / monthlyPlans.length) * 100)
      : 0;

    const delays = completedMonthlyPlans
      .map((plan) => {
        const rawDate = plan.date_soumission || plan.date_upload;
        if (!rawDate) return null;
        const submittedDate = new Date(rawDate);
        return Math.max(0, Math.round((now.getTime() - submittedDate.getTime()) / (1000 * 60 * 60 * 24)));
      })
      .filter((value) => value !== null);

    const delay = delays.length > 0
      ? Math.round(delays.reduce((sum, value) => sum + value, 0) / delays.length)
      : 0;

    return { rate, delay };
  }, [plans]);

  const statsSummary = useMemo(() => {
    const analyzed = plans.filter((plan) => toDisplayStatus(plan.statusKey || plan.statut) === 'analyse').length;
    const validated = plans.filter((plan) => (plan.statusKey || plan.statut) === 'validé').length;
    const rejected = plans.filter((plan) => (plan.statusKey || plan.statut) === 'non validé').length;
    const pendingReview = analyzed;

    return [
      { label: 'Total plans', value: plans.length },
      { label: 'En analyse', value: analyzed },
      { label: 'Valides', value: validated },
      { label: 'Refuses', value: rejected },
      { label: 'A traiter', value: pendingReview },
      { label: 'Filtre courant', value: filteredPlans.length },
    ];
  }, [plans, filteredPlans.length, toDisplayStatus]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    const sectionMap = [
      { key: 'plans', ref: plansSectionRef },
      { key: 'stats', ref: statsSectionRef },
      { key: 'form', ref: validationSectionRef },
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
          .forEach((entry) => {
            const match = sectionMap.find((item) => item.ref.current === entry.target);
            if (match) {
              setActiveSection(match.key);
            }
          });
      },
      {
        root: null,
        threshold: [0.25, 0.45, 0.7],
      }
    );

    sectionMap.forEach((item) => {
      if (item.ref.current) {
        observer.observe(item.ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSaveGrid = async (payload) => {
    if (!selectedPlan) return;

    setStatusUpdating(true);
    try {
      const response = await apiFetch(`/api/plans/${selectedPlan.id}/grille-data`, {
        method: 'PUT',
        auth: true,
        json: true,
        body: JSON.stringify({
          payload,
          statut: selectedPlan.statusKey || 'analyse'
        })
      });

      if (!response.ok) throw new Error('Erreur lors de la sauvegarde');

      showNotice('Validation pedagogique enregistree avec succes.', 'success');
      setRefreshToken((current) => current + 1);
    } catch (err) {
      showNotice('Erreur lors de l\'enregistrement de la validation pedagogique.', 'error');
      console.error(err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleStatusChange = async (statut, payload) => {
    if (!selectedPlan) return;

    const recommendation = statut === 'validé' ? 'approuvé' : 'rejeté';
    const updatedPayload = {
      ...(payload || {}),
      conformite: {
        ...payload?.conformite,
        conseiller_recommandation: recommendation,
      },
    };

    setStatusUpdating(true);
    try {
      const response = await apiFetch(`/api/plans/${selectedPlan.id}/grille-data`, {
        method: 'PUT',
        auth: true,
        json: true,
        body: JSON.stringify({
          payload: updatedPayload,
          statut,
        })
      });

      if (!response.ok) throw new Error('Erreur lors de la sauvegarde');

      showNotice(statut === 'validé' ? 'Plan valide avec succes.' : 'Plan refuse avec succes.', 'success');
      setRefreshToken((current) => current + 1);
    } catch (err) {
      showNotice('Erreur lors de la mise a jour du plan.', 'error');
      console.error(err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const getStatusBadge = (statusKey) => {
    const displayStatus = toDisplayStatus(statusKey);
    const statuses = {
      'brouillon': { label: 'Brouillon', class: 'status-draft' },
      'soumis': { label: 'Soumis', class: 'status-submitted' },
      'analyse': { label: 'En analyse', class: 'status-analyzed' },
      'validé': { label: 'Validé', class: 'status-validated' },
      'approuvé': { label: 'Approuvé', class: 'status-approved' },
      'non validé': { label: 'Non validé', class: 'status-rejected' }
    };
    const status = statuses[displayStatus] || { label: displayStatus, class: 'status-unknown' };
    return <span className={`status-badge ${status.class}`}>{status.label}</span>;
  };

  if (loading) return <div className="dashboard-loading">Chargement des plans...</div>;
  if (error) return <div className="dashboard-error">{error}</div>;

  const renderTimeline = () => {
    if (!timelinePlan) return null;
    
    const conformite = timelinePlanData?.payload?.conformite || {};
    const plan = timelinePlan;
    
    // Tous les plans ici sont soumis par des chefs d'équipe
    // Étapes du processus simplifiées
    const steps = [
      {
        label: 'Plan Soumis par le Chef d\'équipe',
        completed: true,
        date: plan.date_soumission ? new Date(plan.date_soumission).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A',
        icon: '✓'
      },
      {
        label: 'Révision par le Conseiller pédagogique',
        completed: conformite.conseiller_pedagogique === 'oui',
        current: !conformite.conseiller_pedagogique,
        date: conformite.conseiller_pedagogique ? new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : null,
        icon: conformite.conseiller_pedagogique === 'oui' ? '✓' : conformite.conseiller_pedagogique === 'non' ? '✗' : '○',
        isActuel: !conformite.conseiller_pedagogique
      },
      {
        label: 'Approbation par la Direction adjointe',
        completed: conformite.directeur_adjoint === 'oui',
        date: null,
        icon: conformite.directeur_adjoint === 'oui' ? '✓' : conformite.directeur_adjoint === 'non' ? '✗' : '○',
        pending: !conformite.directeur_adjoint
      }
    ];

    return (
      <div className="timeline-modal-overlay" onClick={handleCloseTimeline}>
        <div className="timeline-modal" onClick={(event) => event.stopPropagation()}>
          <div className="timeline-header">
            <h3>Chronologie du processus</h3>
            <button 
              className="btn-close-timeline"
              onClick={handleCloseTimeline}
              title="Fermer"
            >
              ✕
            </button>
          </div>

          <div className="plan-info-timeline">
            <h4>{plan.numero_cours}</h4>
            <p>{plan.titre}</p>
          </div>

          <div className="timeline">
            {steps.map((step, index) => (
              <div 
                key={index} 
                className={`timeline-item ${step.completed ? 'completed' : ''} ${step.current ? 'current' : ''} ${step.pending ? 'pending' : ''} ${step.isActuel ? 'actuel' : ''}`}
              >
                <div className="timeline-icon">
                  {step.icon}
                </div>
                <div className="timeline-content">
                  <div className="timeline-label">{step.label}</div>
                  {step.date && (
                    <div className="timeline-date">
                      Terminé • {step.date}
                    </div>
                  )}
                  {step.isActuel && (
                    <div className="timeline-badge actuel-badge">Actuel</div>
                  )}
                  {step.pending && !step.current && (
                    <div className="timeline-badge pending-badge">À venir</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="conseiller-dashboard">
      <Sidebar
        active={activeSection}
        onNavigate={handleNavigate}
        recentItems={sidebarRecentItems}
        performanceRate={monthlyPerformance.rate}
        performanceDelay={monthlyPerformance.delay}
        showHistory={false}
      />

      <section className="conseiller-main">
        <header className="dashboard-header">
          <div className="header-brand-group">
            <img className="header-brand-icon" src={BRAND_ASSETS.iconMini} alt="" aria-hidden="true" />
            <div>
              <h2>📚 Plans en analyse - Validation pédagogique</h2>
              <p className="muted">Consulter et compléter la validation pédagogique des plans en analyse</p>
            </div>
          </div>
          <div className="header-user">
            <button
              className="conseiller-refresh-btn"
              onClick={handleRefresh}
              type="button"
              disabled={loading}
            >
              {loading ? 'Rafraîchissement...' : 'Rafraîchir'}
            </button>
            <span>{user?.nom || 'Utilisateur'}</span>
            <div className="avatar">{user?.nom?.slice(0, 1) || 'U'}</div>
            {showInlineLogout && (
              <button
                className="conseiller-logout-btn"
                onClick={logout}
                type="button"
              >
                Deconnexion
              </button>
            )}
          </div>
        </header>

      <div className="dashboard-filters">
        <div className="filter-group">
          <label>Statut</label>
          <select 
            value={filters.status} 
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">Tous les statuts</option>
            <option value="analyse">En analyse</option>
            <option value="validé">Validé</option>
            <option value="non validé">Non validé</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Enseignant</label>
          <input
            type="text"
            placeholder="Rechercher par enseignant..."
            value={filters.teacher}
            onChange={(e) => handleFilterChange('teacher', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Chef d'équipe</label>
          <input
            type="text"
            placeholder="Rechercher par chef d'équipe..."
            value={filters.chef}
            onChange={(e) => handleFilterChange('chef', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Cours</label>
          <input
            type="text"
            placeholder="Rechercher par cours..."
            value={filters.course}
            onChange={(e) => handleFilterChange('course', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Session</label>
          <select 
            value={filters.session} 
            onChange={(e) => handleFilterChange('session', e.target.value)}
          >
            <option value="">Toutes les sessions</option>
            {sessions.filter(s => s !== 'Toutes').map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Programme</label>
          <select 
            value={filters.programme} 
            onChange={(e) => handleFilterChange('programme', e.target.value)}
          >
            <option value="">Tous les programmes</option>
            {programmes.filter(p => p !== 'Tous').map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Mode</label>
          <select 
            value={filters.mode} 
            onChange={(e) => handleFilterChange('mode', e.target.value)}
          >
            <option value="">Tous les modes</option>
            {modes.filter(m => m !== 'Tous').map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Campus</label>
          <select 
            value={filters.campus} 
            onChange={(e) => handleFilterChange('campus', e.target.value)}
          >
            <option value="">Tous les campus</option>
            {campus.filter(c => c !== 'Tous').map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="dashboard-loading">Chargement des plans...</p>
      ) : error ? (
        <p className="dashboard-error">{error}</p>
      ) : (
        <>
          <section className="plans-list" ref={plansSectionRef}>
        {filteredPlans.length === 0 ? (
          <div className="no-plans">
            <p>Aucun plan en analyse trouvé.</p>
          </div>
        ) : (
          <div className="plans-table-container">
            <table className="plans-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th><span className="sort-label">Cours <span className="sort-indicator" aria-hidden="true">{getSortIndicator('titre')}</span></span></th>
                  <th><span className="sort-label">Enseignant <span className="sort-indicator" aria-hidden="true">{getSortIndicator('enseignant_nom')}</span></span></th>
                  <th><span className="sort-label">Chef d'équipe <span className="sort-indicator" aria-hidden="true">{getSortIndicator('chef_equipe_nom')}</span></span></th>
                  <th><span className="sort-label">Session <span className="sort-indicator" aria-hidden="true">{getSortIndicator('session')}</span></span></th>
                  <th><span className="sort-label">Programme <span className="sort-indicator" aria-hidden="true">{getSortIndicator('programme')}</span></span></th>
                  <th><span className="sort-label">Mode <span className="sort-indicator" aria-hidden="true">{getSortIndicator('mode_enseignement')}</span></span></th>
                  <th><span className="sort-label">Campus <span className="sort-indicator" aria-hidden="true">{getSortIndicator('campus')}</span></span></th>
                  <th><span className="sort-label">Date de soumission <span className="sort-indicator" aria-hidden="true">{getSortIndicator('date_soumission')}</span></span></th>
                  <th><span className="sort-label">Statut <span className="sort-indicator" aria-hidden="true">{getSortIndicator('statusKey')}</span></span></th>
                  <th>Actions</th>
                </tr>
                <tr className="sort-row">
                  <th></th>
                  <th>
                    <select value={filters.sortBy === 'titre' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('titre', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'enseignant_nom' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('enseignant_nom', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'chef_equipe_nom' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('chef_equipe_nom', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'session' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('session', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'programme' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('programme', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'mode_enseignement' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('mode_enseignement', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'campus' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('campus', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'date_soumission' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('date_soumission', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="desc">Plus récent</option>
                      <option value="asc">Plus ancien</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'statusKey' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('statusKey', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">Priorité haute</option>
                      <option value="desc">Priorité basse</option>
                    </select>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginatedPlans.map(plan => (
                  <tr 
                    key={plan.id}
                    className={selectedPlan?.id === plan.id ? 'selected-row' : ''}
                    onClick={() => handleSelectPlan(plan)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleSelectPlan(plan);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Sélectionner le plan ${plan.numero_cours || plan.titre || plan.id}`}
                  >
                    <td>#{plan.id}</td>
                    <td>
                      <div className="course-info">
                        <strong>{plan.numero_cours}</strong>
                        <span className="course-title">{plan.titre}</span>
                      </div>
                    </td>
                    <td>{plan.enseignant_nom || '—'}</td>
                    <td>
                      {plan.chef_equipe_nom ? (
                        plan.chef_equipe_nom
                      ) : (
                        <span style={{
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          Chef d'équipe
                        </span>
                      )}
                    </td>
                    <td>{plan.session || '—'}</td>
                    <td>{plan.programme || '—'}</td>
                    <td>{plan.mode_enseignement || '—'}</td>
                    <td>{plan.campus || '—'}</td>
                    <td>{plan.date_soumission ? new Date(plan.date_soumission).toLocaleDateString() : '—'}</td>
                    <td>{getStatusBadge(plan.statusKey)}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn-view-pdf"
                          onClick={(e) => {
                            e.stopPropagation();
                            openOrDownloadDocument({
                              url: apiUrl(`/static/plans/${plan.id}_${safeFilename(plan.nom_fichier || '')}`),
                              fileName: plan.nom_fichier,
                              onOpenPdf: () => window.open(apiUrl(`/static/plans/${plan.id}_${safeFilename(plan.nom_fichier || '')}`), '_blank', 'noopener,noreferrer'),
                              onNotify: showNotice,
                            });
                          }}
                          title="Visualiser le document"
                        >
                          📄 Visualiser
                        </button>
                        <button 
                          className="btn-open-timeline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenTimeline(plan);
                          }}
                          title="Afficher la chronologie du processus"
                        >
                          🕓 Chronologie
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </section>

      <section className="conseiller-stats-section" ref={statsSectionRef}>
        <div className="stats-section-header">
          <h3>Statistiques</h3>
          <p>Vue synthese de la validation pedagogique.</p>
        </div>
        <div className="dashboard-stats">
          {statsSummary.map((item) => (
            <div className="stat-card" key={item.label}>
              <h3>{item.value}</h3>
              <p>{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="conseiller-validation-section" ref={validationSectionRef}>
        <div className="conseiller-validation-header">
          <div>
            <h3>Formulaire de validation pédagogique</h3>
            <p>
              {selectedPlan
                ? 'Complétez la grille du plan sélectionné ci-dessous.'
                : 'Sélectionnez un plan dans la file puis cliquez sur Formulaire pour commencer la validation.'}
            </p>
          </div>
          {selectedPlan && (
            <button
              type="button"
              className="conseiller-selection-chip"
              onClick={() => setSelectedPlan(null)}
            >
              Fermer le formulaire
            </button>
          )}
        </div>

        <ValidationInterface
          plan={selectedPlan ? {
            id: selectedPlan.id,
            title: selectedPlan.titre || selectedPlan.numero_cours || selectedPlan.cours,
            course: selectedPlan.numero_cours || selectedPlan.cours || 'N/A',
            teacher: selectedPlan.enseignant_nom || selectedPlan.chef_equipe_nom || '—',
            statusLabel: selectedPlan.statusKey || 'analyse',
          } : null}
          onStatusChange={handleStatusChange}
          statusUpdating={statusUpdating}
          onSaveGrid={handleSaveGrid}
          initialPayload={planPayload}
          loadingPayload={loadingPayload}
          verifierName={user?.nom || ''}
          validationRole="conseiller"
          saveButtonLabel="💾 Enregistrer la validation pédagogique"
          headerTitle="Formulaire de validation pédagogique"
          headerSubtitle="Sélectionnez un plan soumis par un chef d’équipe dans la file pour compléter la validation pédagogique."
          hideResultButton={true}
        />
      </section>
        </>
      )}
      </section>

      {/* Timeline sidebar - Position fixed */}
      {renderTimeline()}
      <NoticeToast notice={notice} onClose={clearNotice} />
    </div>
  );
}
